// SCR-STAFF-930 — بخش‌ها (US-ORG-001): درخت بخش‌های هر شعبه (تا سه سطح — AC-ORG-001.3)، افزودن بخش و زیرشاخه، ویرایش نام، والد،
// مسئول (از کارکنان IAM — AC-ORG-001.1) و وضعیت. بخش «عمومی» هر شعبه خودکار است (AC-ORG-001.2).
import { zodResolver } from '@hookform/resolvers/zod';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SubdirectoryArrowLeftIcon from '@mui/icons-material/SubdirectoryArrowLeft';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { unwrap, type DepartmentView } from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  applyServerErrors,
  CheckboxRHF,
  DataTable,
  EmptyState,
  FormDialog,
  ListPage,
  SelectRHF,
  StatusChip,
  TextFieldRHF,
  zRequiredText,
  type DataTableColumn,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { WriteNotice } from '../settings/common';
import { BranchFilter, ORG_CODE_PATTERN, orgKeys, useBranchSelection } from './common';

export const SCR_STAFF_930 = 'SCR-STAFF-930';
const MAX_DEPTH = 3;

/** ترتیب درختی (والد پیش از فرزندان) با عمق برای تورفتگی. */
export function treeOrder(departments: readonly DepartmentView[]): DepartmentView[] {
  const byParent = new Map<string | null, DepartmentView[]>();
  const ids = new Set(departments.map((d) => d.id));
  for (const department of departments) {
    const parent = department.parentId && ids.has(department.parentId) ? department.parentId : null;
    byParent.set(parent, [...(byParent.get(parent) ?? []), department]);
  }
  const sorted: DepartmentView[] = [];
  const visit = (parent: string | null) => {
    const children = [...(byParent.get(parent) ?? [])].sort(
      (a, b) => Number(b.isDefault) - Number(a.isDefault) || a.code.localeCompare(b.code),
    );
    for (const child of children) {
      sorted.push(child);
      visit(child.id);
    }
  };
  visit(null);
  return sorted;
}

/** بخش و نوادگانش (برای حذف از گزینه‌های والد). */
function subtree(departments: readonly DepartmentView[], id: string): Set<string> {
  const result = new Set<string>([id]);
  let changed = true;
  while (changed) {
    changed = false;
    for (const department of departments) {
      if (department.parentId && result.has(department.parentId) && !result.has(department.id)) {
        result.add(department.id);
        changed = true;
      }
    }
  }
  return result;
}

export function DepartmentsPage() {
  const { t } = useTranslation('org');
  const { tenantApi, session } = useTenantSession();
  const write = useWriteAccess('org.department.manage');
  const branches = useBranchSelection();
  const [editing, setEditing] = useState<{
    department: DepartmentView | null;
    parentId: string | null;
  } | null>(null);

  const departments = useQuery({
    queryKey: orgKeys.departments(session?.tenantId, branches.branchId),
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/organization/departments', {
          params: { query: { branchId: branches.branchId } },
        }),
      ),
    enabled: Boolean(branches.branchId),
  });
  const rows = useMemo(() => treeOrder(departments.data?.items ?? []), [departments.data]);

  const columns: DataTableColumn<DepartmentView>[] = [
    {
      id: 'name',
      headerKey: 'org:departments.columns.name',
      cell: (row) => (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ pl: (Number(row.depth) - 1) * 3 }}
          data-testid={`department-row-${row.code}`}
          data-depth={row.depth}
        >
          {Number(row.depth) > 1 && <SubdirectoryArrowLeftIcon fontSize="small" color="action" aria-hidden />}
          <span>{row.name}</span>
          {row.isDefault && <Chip size="small" variant="outlined" label={t('org:departments.default')} />}
        </Stack>
      ),
    },
    {
      id: 'code',
      headerKey: 'org:departments.columns.code',
      cell: (row) => <bdi dir="ltr">{row.code}</bdi>,
    },
    {
      id: 'head',
      headerKey: 'org:departments.columns.head',
      cell: (row) => row.headName ?? t('common:states.none'),
    },
    {
      id: 'status',
      headerKey: 'org:departments.columns.status',
      cell: (row) => <StatusChip tone={row.isActive ? 'active' : 'inactive'} />,
    },
    {
      id: 'actions',
      headerKey: 'org:departments.columns.actions',
      cell: (row) => {
        const canAddChild = Number(row.depth) < MAX_DEPTH;
        return (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title={t('org:departments.edit')}>
              <span>
                <IconButton
                  aria-label={`${t('org:departments.edit')} ${row.name}`}
                  onClick={() => setEditing({ department: row, parentId: row.parentId ?? null })}
                  disabled={!write.canWrite}
                  data-testid={`department-edit-${row.code}`}
                >
                  <EditOutlinedIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={canAddChild ? t('org:departments.add_child') : t('org:departments.depth_limit')}>
              <span>
                <IconButton
                  aria-label={`${t('org:departments.add_child')} ${row.name}`}
                  onClick={() => setEditing({ department: null, parentId: row.id })}
                  disabled={!write.canWrite || !canAddChild}
                  data-testid={`department-add-child-${row.code}`}
                >
                  <AddIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        );
      },
    },
  ];

  return (
    <ListPage
      screenId={SCR_STAFF_930}
      titleKey="org:departments.title"
      subtitleKey="org:departments.subtitle"
      actions={
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setEditing({ department: null, parentId: null })}
          disabled={!write.canWrite || !branches.branchId}
          data-testid="department-create"
        >
          {t('org:departments.add_root')}
        </Button>
      }
      filters={
        <Stack spacing={1}>
          <BranchFilter
            options={branches.options}
            value={branches.branchId}
            onChange={branches.setBranchId}
          />
          <WriteNotice write={write} />
        </Stack>
      }
    >
      {branches.options.length === 0 ? (
        <EmptyState titleKey="org:no_branch" />
      ) : (
        <DataTable
          captionKey="org:departments.caption"
          columns={columns}
          rows={rows}
          getRowId={(row) => row.id}
          isLoading={departments.isPending}
          error={departments.error}
          onRetry={() => void departments.refetch()}
          emptyTitleKey="org:departments.empty"
        />
      )}
      {editing && branches.branchId && (
        <DepartmentFormDialog
          department={editing.department}
          parentId={editing.parentId}
          branchId={branches.branchId}
          departments={departments.data?.items ?? []}
          onClose={() => setEditing(null)}
        />
      )}
    </ListPage>
  );
}

const departmentSchema = z.object({
  code: z.string(),
  name: zRequiredText(200),
  parentId: z.string(),
  headMembershipId: z.string(),
  isActive: z.boolean(),
});
type DepartmentForm = z.input<typeof departmentSchema>;

function DepartmentFormDialog({
  department,
  parentId,
  branchId,
  departments,
  onClose,
}: {
  /** null ← ایجاد. */
  department: DepartmentView | null;
  parentId: string | null;
  branchId: string;
  departments: readonly DepartmentView[];
  onClose: () => void;
}) {
  const { t } = useTranslation('org');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [submitError, setSubmitError] = useState<unknown>();
  const creating = department === null;
  const canListMembers = access.can('iam.member.view');

  const schema = useMemo(
    () =>
      departmentSchema.superRefine((value, ctx) => {
        if (creating && !ORG_CODE_PATTERN.test(value.code.trim())) {
          ctx.addIssue({ code: 'custom', path: ['code'], message: 'errors:validation.org_code' });
        }
      }),
    [creating],
  );
  const { control, handleSubmit, formState, setError } = useForm<DepartmentForm>({
    resolver: zodResolver(schema),
    defaultValues: {
      code: department?.code ?? '',
      name: department?.name ?? '',
      parentId: (department ? department.parentId : parentId) ?? '',
      headMembershipId: department?.headMembershipId ?? '',
      isActive: department?.isActive ?? true,
    },
  });

  const members = useQuery({
    queryKey: orgKeys.members(session?.tenantId),
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/identity/memberships', {
          params: { query: { status: 'Active', limit: 200 } },
        }),
      ),
    enabled: canListMembers,
    staleTime: 60_000,
  });

  const excluded = department ? subtree(departments, department.id) : new Set<string>();
  const parentOptions = treeOrder(departments).filter(
    (candidate) => !excluded.has(candidate.id) && Number(candidate.depth) < MAX_DEPTH,
  );
  const headOptions = [
    { value: '', labelKey: 'org:departments.form.head_none' },
    ...(members.data?.items ?? []).map((member) => ({
      value: member.membershipId,
      label: member.displayName,
    })),
    ...(department?.headMembershipId &&
    !(members.data?.items ?? []).some((member) => member.membershipId === department.headMembershipId)
      ? [{ value: department.headMembershipId, label: department.headName ?? department.headMembershipId }]
      : []),
  ];

  const save = useMutation({
    mutationFn: (form: DepartmentForm) =>
      creating
        ? unwrap(
            tenantApi.POST('/api/v1/organization/departments', {
              body: {
                branchId,
                code: form.code.trim(),
                name: form.name,
                parentId: form.parentId || null,
                headMembershipId: form.headMembershipId || null,
                isActive: true,
              },
              headers: { 'Idempotency-Key': idempotency.current() },
            }),
          )
        : unwrap(
            tenantApi.PUT('/api/v1/organization/departments/{id}', {
              params: { path: { id: department.id } },
              body: {
                name: form.name,
                parentId: form.parentId || null,
                headMembershipId: form.headMembershipId || null,
                isActive: form.isActive,
                rowVersion: department.rowVersion,
              },
            }),
          ),
    onMutate: () => setSubmitError(undefined),
    onSettled: (_data, error) => {
      if (creating) {
        idempotency.settle(error ?? undefined);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['organization', 'departments'] });
      onClose();
    },
    onError: (error) => {
      const applied = applyServerErrors(error, setError, {
        fields: ['code', 'name', 'parentId', 'headMembershipId', 'isActive'],
        aliases: { head_membership_id: 'headMembershipId', parent_id: 'parentId' },
        codeFields: {
          'PHX-ORG-0101': 'code',
          'PHX-ORG-0102': 'parentId',
          'PHX-ORG-0103': 'parentId',
          'PHX-ORG-0104': 'headMembershipId',
        },
      });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  const titleKey = creating
    ? parentId
      ? 'org:departments.create_child_title'
      : 'org:departments.create_title'
    : 'org:departments.edit_title';

  return (
    <FormDialog
      open
      screenId="SCR-STAFF-930-FORM"
      titleKey={titleKey}
      onSubmit={() => void handleSubmit((form) => save.mutate(form))()}
      onClose={onClose}
      isDirty={formState.isDirty}
      isSubmitting={save.isPending}
      submitError={submitError}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextFieldRHF
          control={control}
          name="code"
          labelKey="org:departments.form.code"
          helperKey={creating ? 'org:departments.form.code_help' : 'org:departments.form.code_immutable'}
          required
          fullWidth
          disabled={!creating}
          slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'department-code' } }}
        />
        <TextFieldRHF
          control={control}
          name="name"
          labelKey="org:departments.form.name"
          required
          fullWidth
          autoFocus
          slotProps={{ htmlInput: { 'data-testid': 'department-name' } }}
        />
      </Stack>
      <SelectRHF
        control={control}
        name="parentId"
        labelKey="org:departments.form.parent"
        helperKey="org:departments.depth_limit"
        fullWidth
        options={[
          { value: '', labelKey: 'org:departments.form.parent_none' },
          ...parentOptions.map((candidate) => ({
            value: candidate.id,
            label: `${'— '.repeat(Number(candidate.depth) - 1)}${candidate.name}`,
          })),
        ]}
      />
      <SelectRHF
        control={control}
        name="headMembershipId"
        labelKey="org:departments.form.head"
        helperKey={canListMembers ? undefined : 'org:departments.form.head_unavailable'}
        disabled={!canListMembers}
        fullWidth
        options={headOptions}
      />
      {!creating && (
        <CheckboxRHF control={control} name="isActive" labelKey="org:departments.form.is_active" />
      )}
      {creating && parentId && (
        <Box>
          <Typography variant="caption" color="text.secondary">
            {t('org:departments.depth_limit')}
          </Typography>
        </Box>
      )}
    </FormDialog>
  );
}
